/**
 * Calculates total fee from selected products, reggora fee, and additional fees
 * @param {Array} selected_products - Array of selected products
 * @param {number|string} reggora_fee - Reggora fee amount
 * @param {Array} additional_fees - Array of additional fees
 * @returns {number} Total fee amount
 */
export const calculateTotalFee = (selected_products, reggora_fee, additional_fees) => {
  let totalFee = 0

  // Add product amounts
  selected_products.forEach((product) => {
    if (product && product.amount) {
      totalFee += parseFloat(product.amount)
    }
  })

  // Add reggora fee
  if (reggora_fee) {
    totalFee += parseFloat(reggora_fee)
  }

  // Add additional fees
  if (additional_fees) {
    additional_fees.forEach((fee) => {
      if (fee.amount && !isNaN(parseFloat(fee.amount))) {
        totalFee += parseFloat(fee.amount)
      }
    })
  }

  return totalFee
}

/**
 * Calculates total fee from products and reggora fee only (excludes additional fees)
 * This is used when additional fees are handled separately
 * @param {Array} selected_products - Array of selected products
 * @param {number|string} reggora_fee - Reggora fee amount
 * @returns {number} Total fee amount (products + reggora fee)
 */
export const calculateProductFee = (selected_products, reggora_fee) => {
  let productTotal = 0

  // Add product amounts
  selected_products.forEach((product) => {
    if (product && product.amount) {
      const amount = parseFloat(product.amount)
      productTotal += amount
    }
  })

  // Add reggora fee to product_amount (this is semantically correct)
  if (reggora_fee) {
    productTotal += parseFloat(reggora_fee)
  }

  return productTotal
}

/**
 * Gets additional product fees from selected products
 * Deduplicates fees by fee_type, keeping only the most expensive fee per type
 * Only includes fees where fee_from === 'borrower'
 * @param {Array} selectedProducts - Array of selected products
 * @returns {Array} Array of additional product fees (deduplicated)
 */
export const getAdditionalProductFees = (selectedProducts) => {
  // Dedupe additional product fees
  // If there are multiple selected products and multiple additional product fees with the same fee_type
  // We only want to apply one fee per fee_type - the most expensive fee
  const maxFees = {}

  selectedProducts.forEach((product) => {
    product &&
      product.additional_fees &&
      product.additional_fees.forEach((fee) => {
        if (fee.fee_from === 'borrower') {
          if (fee.fee_type in maxFees) {
            if (fee.amount > maxFees[fee.fee_type].amount) {
              maxFees[fee.fee_type] = {
                amount: fee.amount,
                description: fee.description,
              }
            }
          } else {
            maxFees[fee.fee_type] = {
              amount: fee.amount,
              description: fee.description,
            }
          }
        }
      })
  })

  const additionalProductFees = Object.keys(maxFees).map((fee_type) => {
    return {
      amount: maxFees[fee_type].amount,
      description: maxFees[fee_type].description,
    }
  })
  return additionalProductFees
}
